// Wishlist that works signed in or out, mirroring the guest cart (guestCart.js).
//
// Signed in: the server wishlist (/api/wishlist, behind authenticate).
// Signed out: product ids in localStorage, hydrated against the public product
// API so the page always shows current titles, prices and images.
//
// On sign-in, mergeGuestWishlist() pushes the saved ids into the account and
// clears this store, so nothing saved as a guest is lost.

import api from "../config/api";

const KEY = "guest_wishlist";

const isSignedIn = () => Boolean(localStorage.getItem("jwt"));

// ---- shared "is this saved?" state for product cards ----
// One fetch per session/account, shared by every card on the page; adds and
// removes (from a card or the product page) update it for all of them.
let idCache = null; // Set of product ids
let idCacheOwner = null; // jwt (or "guest") the cache belongs to
let idLoading = null;
const listeners = new Set();
const owner = () => localStorage.getItem("jwt") || "guest";
const notify = () => listeners.forEach((fn) => fn(new Set(idCache || [])));

export function subscribeWishlistIds(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function loadWishlistIds() {
  if (idCache && idCacheOwner === owner()) return Promise.resolve(new Set(idCache));
  if (idLoading && idCacheOwner === owner()) return idLoading;
  idCacheOwner = owner();
  idLoading = (isSignedIn()
    ? api.get("/api/wishlist").then(({ data }) => (Array.isArray(data) ? data.map((p) => String(p._id)) : []))
    : Promise.resolve(readIds())
  )
    .catch(() => [])
    .then((list) => {
      idCache = new Set(list);
      idLoading = null;
      notify();
      return new Set(idCache);
    });
  return idLoading;
}

const cacheSet = (productId, saved) => {
  if (!idCache || idCacheOwner !== owner()) return;
  if (saved) idCache.add(String(productId));
  else idCache.delete(String(productId));
  notify();
};

function readIds() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(raw) ? raw.filter(Boolean).map(String) : [];
  } catch (e) {
    return []; // malformed/unavailable storage — behave as an empty wishlist
  }
}

function writeIds(ids) {
  try {
    localStorage.setItem(KEY, JSON.stringify([...new Set(ids)]));
  } catch (e) {
    /* storage full or blocked (private mode) — the wishlist just won't persist */
  }
}

// Products for the saved ids. A product deleted since it was saved is dropped
// from storage too, so it doesn't linger as a dead entry.
async function hydrate(ids) {
  const results = await Promise.all(
    ids.map((id) =>
      api
        .get(`/api/products/id/${id}`)
        .then(({ data }) => data)
        .catch((e) => (e.response?.status === 404 ? null : undefined))
    )
  );
  // null = product gone (404); undefined = network/rate-limit error, keep the id.
  const keep = ids.filter((_, i) => results[i] !== null);
  if (keep.length !== ids.length) writeIds(keep);
  return results.filter(Boolean);
}

export async function getWishlist() {
  if (isSignedIn()) {
    const { data } = await api.get("/api/wishlist");
    return Array.isArray(data) ? data : [];
  }
  return hydrate(readIds());
}

export async function isInWishlist(productId) {
  if (isSignedIn()) {
    const list = await getWishlist();
    return list.some((p) => p._id === productId);
  }
  return readIds().includes(String(productId));
}

export async function addToWishlist(productId) {
  if (isSignedIn()) {
    await api.put(`/api/wishlist/add/${productId}`);
  } else {
    writeIds([...readIds(), String(productId)]);
  }
  cacheSet(productId, true);
}

// Returns the updated list of products, which the wishlist page renders.
export async function removeFromWishlist(productId) {
  if (isSignedIn()) {
    const { data } = await api.delete(`/api/wishlist/${productId}`);
    cacheSet(productId, false);
    return Array.isArray(data) ? data : null;
  }
  writeIds(readIds().filter((id) => id !== String(productId)));
  cacheSet(productId, false);
  return null; // caller filters its local list
}

// Push the guest wishlist into the signed-in account, then clear it.
// Called right after a successful sign-in / registration.
export async function mergeGuestWishlist() {
  const ids = readIds();
  if (!ids.length || !isSignedIn()) return;
  for (const id of ids) {
    try {
      await api.put(`/api/wishlist/add/${id}`);
    } catch (e) {
      // Product removed since it was saved — skip it, keep merging the rest.
    }
  }
  try {
    localStorage.removeItem(KEY);
  } catch (e) {
    /* ignore */
  }
  idCache = null; // the account's list changed — refetch on next use
  idCacheOwner = null;
}
